<template>
  <div v-if="locationParam" class="sp-location-provider-search" :class="classModifiers" part="base">
    <div class="sp-location-provider-search__fields">
      <!--
        `value` is a static seed, never a binding: sp-select owns its selection as a live property,
        and Vue re-applies a bare `value` binding on every render (patchElement's `key === "value"`
        carve-out), writing that live selection. This host has nothing to push — it only reads the
        selection back from `input` to build the finder URL. The empty seed selects "Any Specialty".
      -->
      <sp-select
        class="sp-location-provider-search__select"
        :items.prop="specialtyItems"
        value=""
        required
        @input="handleSpecialtyInput"
      ></sp-select>
    </div>
    <sp-button class="sp-location-provider-search__action" variant="outlined" color="primary" @click="handleSearch">
      Search
    </sp-button>
  </div>
</template>

<script setup>
import { computed, ref } from "vue";
import { toJSON } from "#ce/utils/props";
import { useBreakpoints } from "#ce/composables/useBreakpoints";
import { navigateTo } from "#/shared/lib/navigation";
import { props as locationProviderSearchProps } from "./props.js";

const props = defineProps(locationProviderSearchProps);

const { breakpointModifiers } = useBreakpoints();
const classModifiers = computed(() => [...breakpointModifiers.value]);

const specialtyItems = computed(() =>
  (toJSON(props.specialties, false) ?? []).map((option) => ({ title: option.label, value: option.value })),
);

// Each option's value is a pipe-delimited `specialty_scope` union of the NUCC-code specialties for
// that label (the finder ORs them together; the pipe is the finder's primary id delimiter, see
// config/defaults.yml physician_search_id_delimiters). "Any Specialty" scopes to the union of every
// listed option rather than returning every provider at the location.
const SPECIALTY_SCOPE_DELIMITER = "|";
const PROVIDER_SEARCH_CONTEXT_PARAM = "provider_search_context";
const SEARCH_WHO_WORKS_HERE_CONTEXT = "search_who_works_here";
const DEPARTMENT_PARAM = "department";
const anySpecialtyScope = computed(() =>
  specialtyItems.value
    .map((item) => item.value)
    .filter(Boolean)
    .join(SPECIALTY_SCOPE_DELIMITER),
);

const selectedSpecialty = ref("");

function handleSpecialtyInput({ detail }) {
  const [value] = detail;
  selectedSpecialty.value = value ?? "";
}

function handleSearch() {
  if (!props.locationParam) {
    return;
  }

  // Resolve against the current origin rather than the document base URL. Embedded pages
  // carry a <base> tag pointing at the canonical host, which would otherwise hijack a
  // relative finder path and send the visitor off the current environment.
  const url = new URL(props.finderPath, window.location.origin);
  url.searchParams.set("location", props.locationParam);
  url.searchParams.set(PROVIDER_SEARCH_CONTEXT_PARAM, SEARCH_WHO_WORKS_HERE_CONTEXT);

  // Scope the destination finder to providers only. The finder otherwise blends Location results
  // alongside providers (show_location_results?), which for a "who works here" search surfaces the
  // facility itself — and other area facilities — as result cards among the providers. The finder
  // reads result_types from the request params, so this keeps who-works-here results provider-only.
  url.searchParams.set("result_types", "Physician");

  // Never let the visitor's persisted search radius truncate a who-works-here roster: distance is a
  // hard server-side filter, while affiliated hospital-based providers render at their OWN practice
  // card, which can be far from both the hospital and the visitor. The roster must be complete (the
  // Health Plan directory is checked against state regulatory requirements): distance-SORTED from the
  // visitor's remembered zip, never distance-FILTERED (combinaut/sparkle-christus#314).
  //
  // `searchDistance` is the finder's own "any" (no-radius) sentinel by default
  // (LocationSearch.ANY_DISTANCE / the filterer's explicit no-op branch); a directory that has
  // dropped its "Any Distance" option passes its largest configured radius instead, since "any"
  // would otherwise be coerced back to the default radius (combinaut/sparkle-christus#307).
  //
  // The finder is storage-first on the results route: a usable stored location REPLACES every URL
  // location param, including distance (DoctorFinder#handleRouteToResultsPage restores
  // location.distance from sessionStorage), so the URL param alone cannot widen a remembered radius —
  // rewrite the stored blob's distance too. The URL param still covers the no-stored-location case,
  // where the finder bootstraps from the URL.
  url.searchParams.set("distance", props.searchDistance);
  widenStoredLocationDistance();

  // Each option (and "Any Specialty") scopes the finder to a union of specialties via specialty_scope.
  const specialtyScope = selectedSpecialty.value || anySpecialtyScope.value;
  if (specialtyScope) {
    url.searchParams.set("specialty_scope", specialtyScope);
  }

  // The page's campus is auto-derived server-side into the `department` scope (pipe-delimited
  // department_ids for that campus). An empty department omits the param, leaving the whole-hospital roster.
  if (props.department) {
    url.searchParams.set(DEPARTMENT_PARAM, props.department);
  }

  navigateTo(url.href);
}

// The finder's remembered-location key (DoctorFinder.LOCATION_SESSION_STORAGE_KEY, shared with
// SpRequiredFiltersModal). Only the distance field is rewritten — zip/lat/lng stay, so results
// remain distance-sorted from the visitor's remembered location.
const LOCATION_SESSION_STORAGE_KEY = "locationFilter";

function widenStoredLocationDistance() {
  try {
    const stored = window.sessionStorage.getItem(LOCATION_SESSION_STORAGE_KEY);
    if (!stored) {
      return;
    }

    const location = JSON.parse(stored);
    location.distance = props.searchDistance;
    window.sessionStorage.setItem(LOCATION_SESSION_STORAGE_KEY, JSON.stringify(location));
  } catch {
    // sessionStorage unavailable or the stored value is corrupt (the finder drops corrupt values
    // itself) — the URL distance still covers the bootstrap-from-URL path.
  }
}
</script>

<style>
:host {
  display: block;
  --border-color: var(--sp-comp-location-provider-search-border-color, var(--sp-sys-color-outline, #a7a8a9));
  --border-radius: var(--sp-comp-location-provider-search-border-radius, var(--sp-ref-border-radius-xs, 4px));
  --gap: var(--sp-comp-location-provider-search-gap, var(--sp-ref-scale-16));
  --padding: var(--sp-comp-location-provider-search-padding, var(--sp-ref-scale-16));
}
</style>

<style scoped lang="scss">
.sp-location-provider-search {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--gap);
  padding: var(--padding);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius);

  &__fields {
    display: flex;
    flex: 1 0 0;
    min-width: 0;
    align-items: center;
    gap: var(--gap);
  }

  &__select {
    flex: 1 0 0;
    min-width: 0;
  }

  &__action {
    flex-shrink: 0;
    --sp-comp-button-border-radius: 100px;
  }

  /* Stack vertically below the md breakpoint (< 992px), matching the design-system breakpoints. */
  &.--is-sm-screen,
  &.--is-md-screen {
    flex-direction: column;
    align-items: stretch;

    .sp-location-provider-search__fields {
      flex-direction: column;
      align-items: stretch;
    }

    .sp-location-provider-search__action {
      width: 100%;
    }
  }
}
</style>
